import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNotification } from './useNotification';

import { INSIGHTS_QUERY_KEY } from '@insights_module/constants/queryKeys';
import { fetchFlightpathSalesAvails } from '@services/index';
import { AQLQuery } from '@insights_module/adc_ql/types';

export type AdPlacementLogicType = 'AND' | 'OR';

type FlightpathAvailsParams = {
  flightpath_params: {
    start_date: string;
    end_date: string;
    insertions: string[];
    cap_days_interval?: string;
    cap_max_impressions?: string;
    ad_placement_logic: AdPlacementLogicType;
  };
  query: AQLQuery;
};

export const useFlightpathAvails = () => {
  const { notify, NTFC_TYPE } = useNotification();

  const [params, setParams] = useState<FlightpathAvailsParams | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totalAvails, setTotalAvails] = useState<number | null>(null);

  useQuery({
    queryKey: [INSIGHTS_QUERY_KEY.flightpath_avails, { params }],
    queryFn: async () => await fetchFlightpathSalesAvails({ ...params }),
    enabled: params !== null,
    staleTime: 0,
    cacheTime: 0,
    onSuccess: (data) => {
      setTotalAvails(data?.data?.total_sales_avails);
    },
    onError: (e: Error & { status?: number; detail?: { message: string } }) => {
      setTotalAvails(null);

      if (e?.status && e.status > 500) {
        notify({
          type: NTFC_TYPE.DANGER,
          message:
            'Request failed due to timeout from flightpath - try again with smaller audio count',
        });
      } else {
        notify({
          type: NTFC_TYPE.DANGER,
          message: e?.detail?.message || 'Request error',
        });
      }
    },
    onSettled: () => {
      setParams(null);
      setIsLoading(false);
    },
  });

  const initiateFlightpathAvails = useCallback((params: FlightpathAvailsParams) => {
    setParams(params);
    setIsLoading(true);
  }, []);

  return {
    totalAvails,
    isLoading,
    initiateFlightpathAvails,
  };
};
